import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  BarChart as Chart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import { useState, useEffect } from "react";
import { useTheme } from "next-themes";

type ChartData = {
  month?: string;
  count?: number;
  label?: string;
  value?: number;
  [key: string]: string | number | undefined; // Allow for dynamic keys with specific types
};

type BarChartProps = {
  title: string;
  data: ChartData[];
  color?: string;
  compact?: boolean;
};

// Custom tooltip component for better styling
interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    dataKey: string;
    value: number;
  }>;
  label?: string;
}

const CustomTooltip = ({ active, payload, label }: TooltipProps) => {
  if (active && payload && payload.length) {
    const valueKey = payload[0].dataKey; // Get the actual data key (count or value)
    return (
      <div className="bg-card border border-border p-3 rounded-md shadow-sm">
        <p className="font-medium text-sm text-foreground">{label}</p>
        <p className="text-sm text-primary font-semibold mt-1">
          {payload[0].value.toLocaleString()}{" "}
          {valueKey === "count" ? "items" : ""}
        </p>
      </div>
    );
  }

  return null;
};

const BarChart = ({
  title,
  data,
  color = "hsl(var(--primary))",
  compact = false,
}: BarChartProps) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [formattedData, setFormattedData] = useState<ChartData[]>([]);
  const [dataKey, setDataKey] = useState<string>("count");
  const [labelKey, setLabelKey] = useState<string>("month");
  const { theme } = useTheme();

  // Detect the best data keys to use based on data format
  useEffect(() => {
    if (!data || data.length === 0) {
      setFormattedData([]);
      return;
    }

    // Sample the first item to determine data structure
    const sample = data[0];
    let valueKey = "count";
    let labelKey = "month";

    // Determine the best value key
    if (sample.count !== undefined) {
      valueKey = "count";
    } else if (sample.value !== undefined) {
      valueKey = "value";
    }

    // Determine the best label key
    if (sample.month !== undefined) {
      labelKey = "month";
    } else if (sample.label !== undefined) {
      labelKey = "label";
    }

    setDataKey(valueKey);
    setLabelKey(labelKey);

    // Create a normalized version of the data
    const normalized = data.map((item) => {
      const value = item[valueKey] !== undefined ? item[valueKey] : 0;
      const label = item[labelKey] || "Unknown";

      return {
        [valueKey]: value,
        [labelKey]: label,
      };
    });

    setFormattedData(normalized);
  }, [data]);

  // Calculate colors based on theme
  const isDarkTheme = theme === "dark";

  // Define base and hover colors with better light mode support
  const defaultBarColor = isDarkTheme
    ? "hsl(215, 90%, 67%)"
    : "hsl(215, 90%, 45%)";
  const defaultBarHoverColor = isDarkTheme
    ? "hsl(var(--primary-foreground))"
    : "hsl(215, 90%, 30%)";

  // Use provided color or defaults
  const barColor =
    color === "hsl(var(--primary))"
      ? isDarkTheme
        ? "hsl(var(--primary))"
        : "hsl(215, 90%, 45%)"
      : color;

  // Helper function to derive a darker version of an HSL color
  const getDarkerHSLColor = (hslColor: string): string => {
    // Extract HSL values from the color string
    const hslMatch = hslColor.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (hslMatch) {
      const h = parseInt(hslMatch[1], 10);
      const s = parseInt(hslMatch[2], 10);
      // Reduce lightness for hover state (make it darker)
      const l = Math.max(parseInt(hslMatch[3], 10) - 15, 20); // Reduce lightness but don't go below 20%
      return `hsl(${h}, ${s}%, ${l}%)`;
    }
    return hslColor;
  };

  // Derive hover color from bar color
  const barHoverColor = isDarkTheme
    ? "hsl(var(--primary-foreground))" // For primary color in dark mode
    : color === "hsl(var(--primary))"
    ? "hsl(215, 90%, 30%)" // Default hover for primary in light mode
    : getDarkerHSLColor(barColor); // Derive hover color from custom color

  // Define grid and axis colors based on theme
  const gridColor = isDarkTheme ? "hsl(var(--border))" : "hsl(220, 13%, 91%)";
  const axisTickColor = isDarkTheme
    ? "hsl(var(--muted-foreground))"
    : "hsl(215, 16%, 47%)";

  const handleMouseEnter = (_: unknown, index: number) => {
    setActiveIndex(index);
  };

  const handleMouseLeave = () => {
    setActiveIndex(null);
  };

  if (!formattedData || formattedData.length === 0) {
    return (
      <Card className={compact ? "w-full" : "col-span-2"}>
        <CardHeader className={compact ? "p-4 pb-0" : undefined}>
          <CardTitle className={compact ? "text-base" : undefined}>
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className={compact ? "p-4 pt-2" : undefined}>
          <div
            className={`${
              compact ? "h-[220px]" : "h-[300px]"
            } flex items-center justify-center text-muted-foreground`}
          >
            No data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={compact ? "w-full" : "col-span-2"}>
      <CardHeader className={compact ? "p-4 pb-0" : undefined}>
        <CardTitle className={compact ? "text-base" : undefined}>
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className={compact ? "p-4 pt-2" : undefined}>
        <div className={compact ? "h-[220px]" : "h-[300px]"}>
          <ResponsiveContainer width="100%" height="100%">
            <Chart
              data={formattedData}
              margin={
                compact
                  ? { top: 5, right: 15, left: 0, bottom: 5 }
                  : { top: 5, right: 30, left: 20, bottom: 5 }
              }
            >
              <XAxis
                dataKey={labelKey}
                axisLine={{ stroke: gridColor }}
                tickLine={{ stroke: gridColor }}
                tick={{ fill: axisTickColor, fontSize: compact ? 10 : 12 }}
                stroke={gridColor}
              />
              <YAxis
                axisLine={{ stroke: gridColor }}
                tickLine={{ stroke: gridColor }}
                tick={{ fill: axisTickColor, fontSize: compact ? 10 : 12 }}
                stroke={gridColor}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{
                  fill: isDarkTheme
                    ? "hsl(var(--accent))"
                    : "hsl(215, 25%, 90%)",
                  opacity: 0.3,
                }}
              />
              <Bar
                dataKey={dataKey}
                name="Count"
                radius={[4, 4, 0, 0]}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {formattedData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={activeIndex === index ? barHoverColor : barColor}
                    cursor="pointer"
                    stroke={
                      isDarkTheme
                        ? "hsl(var(--background))"
                        : "hsl(var(--background))"
                    }
                    strokeWidth={1}
                  />
                ))}
              </Bar>
            </Chart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default BarChart;
